/**
 * MyPage Modal Component
 * Displays user's points and handle name settings
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedApiMessage } from '../lib/api-error-message';
import { apiClient } from '../lib/api-client';
import { ROUTES } from '../lib/constants';

interface MyPageModalProps {
    isOpen: boolean;
    onClose: () => void;
    points: number | null;
    username: string;
    handleName?: string | null;
    onPointsClick: () => void;
    onHandleNameUpdate?: (newHandleName: string) => void;
}

export default function MyPageModal({
    isOpen,
    onClose,
    points,
    username,
    handleName,
    onPointsClick,
    onHandleNameUpdate,
}: MyPageModalProps) {
    const { t } = useTranslation('common');
    const [isEditingName, setIsEditingName] = useState(false);
    const [newHandleName, setNewHandleName] = useState(handleName || username);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Update local state when handleName prop changes
    useEffect(() => {
        setNewHandleName(handleName || username);
    }, [handleName, username]);

    if (!isOpen) return null;

    const displayName = handleName || username || t('mypage.guestUser');

    const handleSaveHandleName = async () => {
        if (!newHandleName.trim()) {
            setError(t('mypage.handleName.required'));
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const response = await apiClient.updateHandleName(newHandleName.trim());
            onHandleNameUpdate?.(response.display_name);
            setIsEditingName(false);
        } catch (err) {
            console.error('Failed to update handle name:', err);
            // NGワードで拒否された場合は該当語を示す（それ以外は従来の汎用メッセージ）
            const message = localizedApiMessage(err, 'mypage.handleName.updateError');
            setError(t(message.key, message.values));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setNewHandleName(handleName || username);
        setIsEditingName(false);
        setError(null);
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <svg width="24" height="24" fill="currentColor" className="text-pink-500" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        {t('mypage.title')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Points Section */}
                    <div className="bg-white border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <svg width="24" height="24" fill="currentColor" className="text-yellow-500" viewBox="0 0 20 20">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            <h3 className="text-lg font-bold text-gray-900">{t('mypage.ownedPoints')}</h3>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 font-semibold">{t('mypage.currentPoints')}</span>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl font-bold text-yellow-500">
                                    {points !== null ? points.toLocaleString() : '---'} pt
                                </span>
                                <button
                                    onClick={onPointsClick}
                                    className="px-4 py-2 bg-pink-500 text-white font-semibold text-sm hover:bg-pink-600 transition-colors"
                                >
                                    {t('mypage.purchasePoints')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Handle Name Section */}
                    <div className="bg-white border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <svg width="24" height="24" fill="currentColor" className="text-pink-500" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <h3 className="text-lg font-bold text-gray-900">{t('mypage.handleName.title')}</h3>
                        </div>
                        <div className="flex flex-col gap-2">
                            {isEditingName ? (
                                <div className="flex flex-col gap-2">
                                    <span className="text-sm text-gray-600 font-semibold">{t('mypage.handleName.current')}</span>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={newHandleName}
                                            onChange={(e) => setNewHandleName(e.target.value)}
                                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 text-gray-900 font-semibold focus:outline-none focus:border-pink-500"
                                            autoFocus
                                            maxLength={50}
                                            disabled={isSaving}
                                        />
                                        <button
                                            onClick={handleSaveHandleName}
                                            disabled={isSaving}
                                            className="px-4 py-2 bg-pink-500 text-white font-semibold text-sm hover:bg-pink-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {isSaving ? t('mypage.handleName.saving') : t('mypage.handleName.save')}
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            disabled={isSaving}
                                            className="px-4 py-2 bg-white border border-gray-300 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {t('mypage.handleName.cancel')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600 font-semibold">{t('mypage.handleName.current')}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-base font-semibold text-gray-900">
                                            {displayName}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setNewHandleName(handleName || username);
                                                setIsEditingName(true);
                                                setError(null);
                                            }}
                                            className="px-4 py-2 bg-pink-500 text-white font-semibold text-sm hover:bg-pink-600 transition-colors whitespace-nowrap"
                                        >
                                            {t('mypage.handleName.change')}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {error && (
                                <div className="text-red-500 text-sm">{error}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                                {t('mypage.handleName.note')}
                            </div>
                        </div>
                    </div>

                    {/* Account Settings Section */}
                    <div className="bg-white border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <svg width="24" height="24" fill="none" stroke="currentColor" className="text-pink-500" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <h3 className="text-lg font-bold text-gray-900">{t('mypage.accountSettings.title')}</h3>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">{t('mypage.accountSettings.description')}</span>
                            <Link
                                to={ROUTES.SETTINGS}
                                onClick={onClose}
                                className="px-4 py-2 bg-pink-500 text-white font-semibold text-sm hover:bg-pink-600 transition-colors whitespace-nowrap"
                            >
                                {t('mypage.accountSettings.link')}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-white border-2 border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        {t('buttons.close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
